/**
 * COMBAT.app's board-authoring toolbar state — the tile brush, the pointer tool, the cover/wall/hazard
 * modifiers, the OBJECTS pick, the sticky erase direction, auto-wall, and the Draw tool + color.
 * All of it is **per-GM client authoring preference, never synced encounter state** (the placement
 * itself is the command), which is why it is colocated here rather than read off the room.
 *
 * The three `pick*` callbacks are the mutual-exclusion rule: the left button has ONE intent, so
 * arming a tile brush, a pointer tool, or Draw disarms the other two. Their identities are stable
 * (`useCallback []`) so the keyboard root's once-subscribed listener can capture them.
 */
import { useCallback, useState, type Dispatch, type SetStateAction } from "react";
import { DEFAULT_HAZARD_TYPE, type AnnotationColor, type DamageType, type TileTypeId } from "@lancer-vtt/domain";
import { DEFAULT_PAINT_TILE, type CombatBrush, type CoverGrade, type WallHeight } from "../combatAuthoring";
import { OBJECT_PRESETS, type ObjectOrientation, type ObjectPick } from "../objectAuthoring";
import type { CombatTool, DrawTool } from "../CombatStage";
import { readPenColor, readPenMode, writePenColor, writePenMode, type PenMode } from "./penPrefs";

export type AuthoringToolbar = {
  brush: CombatBrush | null;
  setBrush: Dispatch<SetStateAction<CombatBrush | null>>;
  tool: CombatTool;
  setTool: Dispatch<SetStateAction<CombatTool>>;
  coverGrade: CoverGrade;
  setCoverGrade: Dispatch<SetStateAction<CoverGrade>>;
  wallHeight: WallHeight;
  setWallHeight: Dispatch<SetStateAction<WallHeight>>;
  wallCoverGrade: CoverGrade;
  setWallCoverGrade: Dispatch<SetStateAction<CoverGrade>>;
  hazard: DamageType;
  setHazard: Dispatch<SetStateAction<DamageType>>;
  tile: TileTypeId;
  setTile: Dispatch<SetStateAction<TileTypeId>>;
  eraseMode: boolean;
  setEraseMode: Dispatch<SetStateAction<boolean>>;
  autoWall: boolean;
  setAutoWall: Dispatch<SetStateAction<boolean>>;
  objectPreset: ObjectPick;
  setObjectPreset: Dispatch<SetStateAction<ObjectPick>>;
  objectSectionCount: number;
  setObjectSectionCount: Dispatch<SetStateAction<number>>;
  objectArmor: number;
  setObjectArmor: Dispatch<SetStateAction<number>>;
  objectOrientation: ObjectOrientation;
  setObjectOrientation: Dispatch<SetStateAction<ObjectOrientation>>;
  objectHeight: WallHeight;
  setObjectHeight: Dispatch<SetStateAction<WallHeight>>;
  drawTool: DrawTool;
  setDrawTool: Dispatch<SetStateAction<DrawTool>>;
  drawColor: AnnotationColor;
  /** Set the next stroke's colour, remembered per viewer (`penPrefs.ts`). */
  setDrawColor: (c: AnnotationColor) => void;
  /** The Pen's mode: what D arms. Arming a Pen mode remembers it. */
  penMode: PenMode;
  pickBrush: (b: CombatBrush | null) => void;
  /** Pick a catalogue tile from the palette and arm the tile brush with it. */
  pickTile: (t: TileTypeId) => void;
  pickTool: (t: CombatTool) => void;
  pickDrawTool: (d: DrawTool) => void;
};

export function useAuthoringToolbar(): AuthoringToolbar {
  // The active tile authoring brush, lifted so the tool rail picks it and the stage paints with it
  // `null` = no tile brush armed.
  const [brush, setBrush] = useState<CombatBrush | null>(null);
  // The pointer tool the rail arms: `pan` makes a left-drag pan
  // the stage; `wall` arms the rubber-band edge-wall tool; `select` (default) moves/selects tokens.
  // The left button has one intent: pan, the wall tool, and a tile brush are mutually exclusive, so
  // arming one clears the others (space/middle always pan regardless). `select` is the base tool a
  // tile brush overlays — arming a brush forces `select` while the brush stays the active gesture.
  const [tool, setTool] = useState<CombatTool>("select");
  // The Cover brush's degree: the model carries `cover: "soft"
  // | "hard"`, only `hard` was authorable before. The TERRAIN rail's soft/hard toggle sets this; the
  // stage threads it into the box/stroke commit. Default hard (the prior behavior).
  const [coverGrade, setCoverGrade] = useState<CoverGrade>("hard");
  // The Wall tool's armed height: the model carries the
  // choice as which flags `set_edge` writes (full = blocksMovement+blocksSight+blocksFlight; low =
  // blocksMovement only). The TERRAIN rail's full/low toggle sets this; the stage threads it into the
  // wall run commit exactly like `coverGrade`. Defaults full (the developer's indoor-walls reading).
  const [wallHeight, setWallHeight] = useState<WallHeight>("full");
  // The low wall's cover grade: a SEPARATE state from
  // `coverGrade` above — that one defaults hard and is shared with the Cover cell brush; this one
  // defaults soft (RAW p.66 names low walls as soft cover) and is threaded into the wall run commit
  // exactly like `wallHeight`. Reusing `coverGrade` would either give low walls a hard default
  // (wrong per D3) or force a default flip that regresses the Cover brush.
  const [wallCoverGrade, setWallCoverGrade] = useState<CoverGrade>("soft");
  // The Dangerous brush's hazard damage type (`dangerous-terrain`): the model
  // carries `hazard: DamageType` on a dangerous cell (kinetic default — `DEFAULT_HAZARD_TYPE`).
  // The TERRAIN rail's hazard-type sub-toggle sets this; the stage threads it into the box/stroke
  // commit exactly like `coverGrade`.
  const [hazard, setHazard] = useState<DamageType>(DEFAULT_HAZARD_TYPE);
  // The tile brush's catalogue tile (`terrain-tile-types` Phase 4): the TERRAIN palette's pick. The
  // stage threads it into the box/stroke commit exactly like `hazard`, and the commit writes
  // `{ tile: id }` cells (`decisions/0185`).
  const [tile, setTile] = useState<TileTypeId>(DEFAULT_PAINT_TILE);
  // The sticky **erase direction**: when on, a paint gesture clears instead of fills — the
  // toggle counterpart to the momentary Alt-held erase (the stage OR-s the two). Bound to the `X` key.
  const [eraseMode, setEraseMode] = useState(false);
  // **Auto-wall mode**: when on, committing a room (create / extend / erase)
  // also walls its new perimeter and clears its interior edges, in the SAME undo step (`roomBoxCommand`
  // / `roomEraseCommand` fold the `set_edge` children). Default ON. A per-GM **client** authoring
  // preference — NOT synced encounter state (it gates which commands the authoring layer composes, it
  // is not itself a command), colocated with the other terrain-toolbar client state (brush / cover /
  // erase) here in CombatApp, exactly like them.
  const [autoWall, setAutoWall] = useState(true);
  // The OBJECTS tool's currently-picked preset/SIZE/armor/orientation — a per-GM client authoring
  // preference, colocated with the other terrain-toolbar state above
  // (never synced encounter state; the placement itself is the command). Defaults to Wall's own
  // defaults so the first click after arming the tool places something sensible.
  const [objectPreset, setObjectPreset] = useState<ObjectPick>("wall");
  const [objectSectionCount, setObjectSectionCount] = useState(OBJECT_PRESETS.wall.defaultSectionCount);
  const [objectArmor, setObjectArmor] = useState(0);
  const [objectOrientation, setObjectOrientation] = useState<ObjectOrientation>("horizontal");
  // The Wall object preset's height (full | low). Its OWN state, not the wall tool's `wallHeight`:
  // the two tools author different things (an edge vs a destructible combatant), and arming low on
  // one shouldn't silently change what the other places. Defaults full, like the wall tool.
  const [objectHeight, setObjectHeight] = useState<WallHeight>("full");
  // The **draw** annotation tool — `null`
  // when Draw isn't armed. Deliberately SEPARATE state from `brush`/`tool` above: those gate through
  // `toolsDisabled` (GM+EDIT only, the terrain rail), while Draw is mode-independent and reachable by
  // GM AND players, in EDIT or PLAY — the ping/cursor precedent, not the Terrain window. Survives an
  // EDIT/PLAY flip (`switchViewMode` below deliberately does NOT clear it) so a GM's pre-fight sketch
  // stays armed across the mode switch, same as it would for a player who has no EDIT to lose.
  const [drawTool, setDrawTool] = useState<DrawTool>(null);
  // The color a fresh annotation is drawn in (`ANNOTATION_COLORS`, defaulting to the one member with
  // no other established combat-surface meaning). A per-client authoring
  // preference, not synced encounter state — the stroke's `color` lands on the annotation ITSELF once
  // drawn, so this only decides what the NEXT stroke looks like.
  const [drawColor, setDrawColorState] = useState<AnnotationColor>(readPenColor);
  const setDrawColor = useCallback((c: AnnotationColor) => {
    writePenColor(c);
    setDrawColorState(c);
  }, []);
  // The Pen's last-used mode (`penPrefs.ts`), which D re-arms.
  const [penMode, setPenMode] = useState<PenMode>(readPenMode);
  // Stable identities (`useCallback`), read by the keyboard root's once-subscribed listener.
  // Draw and the BUILD tools are mutually exclusive: arming
  // either disarms the other, so they never fight for the left button on the stage.
  const pickBrush = useCallback((b: CombatBrush | null) => {
    setBrush(b);
    if (b !== null) {
      setTool("select"); // a tile brush disarms pan / the wall tool
      setDrawTool(null); // …and disarms Draw
    }
  }, []);
  const pickTile = useCallback(
    (t: TileTypeId) => {
      setTile(t);
      pickBrush("tile");
    },
    [pickBrush],
  );
  const pickTool = useCallback((t: CombatTool) => {
    setTool(t);
    if (t !== "select") {
      setBrush(null); // arming pan or the wall tool disarms the tile brush
      setDrawTool(null); // …and disarms Draw
    }
  }, []);
  const pickDrawTool = useCallback((d: DrawTool) => {
    setDrawTool(d);
    if (d === "freehand" || d === "polyline") {
      writePenMode(d);
      setPenMode(d);
    }
    if (d !== null) {
      setBrush(null); // arming Draw disarms the terrain rail's tile brush…
      setTool("select"); // …and its pointer tool (wall/room/object all fall back to select)
    }
  }, []);

  return {
    brush,
    setBrush,
    tool,
    setTool,
    coverGrade,
    setCoverGrade,
    wallHeight,
    setWallHeight,
    wallCoverGrade,
    setWallCoverGrade,
    hazard,
    setHazard,
    tile,
    setTile,
    eraseMode,
    setEraseMode,
    autoWall,
    setAutoWall,
    objectPreset,
    setObjectPreset,
    objectSectionCount,
    setObjectSectionCount,
    objectArmor,
    setObjectArmor,
    objectOrientation,
    setObjectOrientation,
    objectHeight,
    setObjectHeight,
    drawTool,
    setDrawTool,
    drawColor,
    setDrawColor,
    penMode,
    pickBrush,
    pickTile,
    pickTool,
    pickDrawTool,
  };
}
